import * as Q from "q";

import { AppCapability, AppContext } from "DistributedTaskControls/Common/AppContext";
import * as Common from "DistributedTaskControls/Common/Common";
import { Feature, Properties, Source, Telemetry } from "DistributedTaskControls/Common/Telemetry";
import { DtcUtils } from "DistributedTaskControls/Common/Utilities";
import * as Resources from "DistributedTaskControls/Resources/TFS.Resources.DistributedTaskControls";
import { InputControlType } from "DistributedTaskControls/SharedControls/InputControls/Common";
import { ConnectedServiceComponentUtility, IConnectedServiceInputStateBase } from
    "DistributedTaskControls/SharedControls/InputControls/Components/ConnectedServiceComponentUtility";
    
import { Boolean } from "DistributedTaskControls/Common/Primitives";
import { StoreManager } from "DistributedTaskControls/Common/Stores/StoreManager";
import { DeployPhaseTypes, ParallelExecutionTypes, ExecutionPlanConstants } from "DistributedTaskControls/Phase/Types";
import { SecureFilesStore } from "DistributedTaskControls/Stores/SecureFilesStore";
import { DefinitionType } from "DistributedTasksCommon/TFS.Tasks.Types";
import { IDefinitionVariable } from "DistributedTaskControls/Variables/Common/Types";

import { TaskDefinition } from "TFS/DistributedTask/Contracts";
import { TaskInputDefinitionBase as TaskInputDefinition } from "TFS/DistributedTaskCommon/Contracts";

import * as Dialogs from "VSS/Controls/Dialogs";
import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";
import { copyToClipboard } from "VSS/Utils/Clipboard";
import { parseInvariant } from "VSS/Utils/Number";
import { empty, newLine, lineFeed, format, startsWith, ignoreCaseComparer } from "VSS/Utils/String";

import "VSS/LoaderPlugins/Css!DistributedTaskControls/Styles/CopyDialogueStyles";

export function showCopyDialog(artifactType: string, yamlResult: string) {
    publishTelemetry(Feature.CopyYaml, artifactType);

    if (FeatureAvailabilityService.isFeatureEnabled("DistributedTask.ViewYamlHasMoved", false)) {
        Dialogs.showMessageDialog(Resources.ViewYamlMessage, {
            title: Resources.ViewYamlTitle,
			buttons: [Dialogs.MessageDialog.buttons.ok]
		});
    }
    else {
        const dialog = Dialogs.show(Dialogs.CopyContentDialog,
            {
                dialogLabel: Resources.CopyContentHelpText,
                dialogLabelExtend: format(Resources.CopyContentHelpDocsLink, Resources.CopyContentHelpDocsLinkTitle),
                data: yamlResult,
                disableEdit: true,
                preventAutoTextSelection: true, // This avoids an issue where NVDA is interrupted and reads the text selection instead of the dialog title
                textAreaCopyClass: "dtc-copy-dialogue-text-area",
                buttons: [{
                    id: Common.KEY_COPY_AS_YAML,
                    text: Resources.CopyToClipboard,
                    click: () => {
                        copyToClipboard(yamlResult);
                        publishTelemetry(Feature.ViewAsYaml, artifactType);
                        dialog.close();
                    }
                }]
            });
    }
}

function publishTelemetry(eventType: string, artifactType: string) {
    const eventProperties: IDictionaryStringTo<string> = {};
    if (artifactType) {
        eventProperties[Properties.ViewYamlArtifact] = artifactType;
    }

    Telemetry.instance().publishEvent(eventType, eventProperties, Source.CommandButton);
}

/**
 * Generate the equivalent YAML for the pipeline process and show in a dialog to allow the user to copy
 * @param jobs An array of pipeline jobs, formerly called phases
 */
export function handleViewProcessAsYaml(jobs: YamlJob[], skipSyncSource: string, cleanOption: string, checkoutFromLFS: string, fetchDepth: string) {
    getProcessSnippet(jobs, skipSyncSource, cleanOption, checkoutFromLFS, fetchDepth).then((content: string) => {
        showCopyDialog(YamlHelperConstants.ProcessCIKey, content);
    });
}

/**
 * Generate the equivalent YAML for the job (formerly called a phase) and show in a dialog to allow the user to copy
 * @param poolName The name of the pool, formerly called a queue
 * @param poolIsHosted If the pool is hosted then it can still be identified by its name, which is different than its vmImage name (i.e. Hosted VS2017 vs. vs2017-win2016)
 */
export function handleViewJobAsYaml(refName: string, name: string, jobType: DeployPhaseTypes, tasks: YamlTask[], demands: any[], poolName: string, poolIsHosted: boolean,
                                    timeoutInMinutes: number, dependencies: string[], condition: string, executionPlanType: ParallelExecutionTypes) {
    const yamlJob: YamlJob = new YamlJob(refName, name, jobType, tasks, demands, poolName, poolIsHosted, timeoutInMinutes, dependencies, condition, executionPlanType);

    yamlJob.getValue(false).then((result: string) => {
        showCopyDialog(YamlHelperConstants.jobCIKey, result);
    });
}

export function getProcessSnippet(jobs: YamlJob[], skipSyncSource: string, cleanOption: string, checkoutFromLFS: string, fetchDepth: string): IPromise<string> {
    let content = "";
    const promises = [];
    jobs = jobs || [];
    jobs.forEach((job: YamlJob) => {
        promises.push(job.getValue(jobs.length > 1));
    });

    if (skipSyncSource !== Boolean.trueString) {
        // adding checkout section
        content += YamlHelperConstants.resourcesKey + YamlHelperConstants.colon + newLine + YamlHelperConstants.repoKey + YamlHelperConstants.colon
            + YamlHelperConstants.singleSpace + YamlHelperConstants.syncSourcesValue + newLine;

        if (cleanOption && cleanOption !== Boolean.falseString) {
            content += indent(YamlHelperConstants.cleanOption) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + cleanOption + newLine;
        }

        if (checkoutFromLFS === Boolean.trueString) {
            content += indent(YamlHelperConstants.lfsOption) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + checkoutFromLFS + newLine;
        }

        const parsedFetchDepth = fetchDepth ? parseInvariant(fetchDepth) : 0;
        if (parsedFetchDepth > 0) {
            content += indent(YamlHelperConstants.fetchDepthOption) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + parsedFetchDepth + newLine;
        }

        content += newLine;
    }

    if (jobs.length > 1) {
        content += YamlHelperConstants.jobsProperty + newLine + newLine;
    }

    return Promise.all(promises).then((yamlJobsContent: string[]) => {
        yamlJobsContent.forEach((yamlJobContent: string) => {
            content += yamlJobContent + newLine;
        });
        return content;
    });
}

export function handleViewTaskAsYaml(inputs: IDictionaryStringTo<string>, taskDefinition: TaskDefinition, taskRefName: string, taskDisplayName: string, taskVersion: string,
                                     enabled: boolean, continueOnError: boolean, condition: string, timeout: number, inputsStates: IDictionaryStringTo<boolean>,
                                     variables: IDictionaryStringTo<IDefinitionVariable>, processParameters: IDictionaryStringTo<string>,
                                     environmentVariables: IDictionaryStringTo<string>, skipSyncSources: boolean, retryCountOnTaskFailure: number) {
    const yamlTask: YamlTask = new YamlTask(inputs, taskDefinition, taskRefName, taskDisplayName, taskVersion, enabled, continueOnError, condition,
                                            timeout, inputsStates, variables, processParameters, environmentVariables, false, 0, skipSyncSources, retryCountOnTaskFailure);
    yamlTask.getValue(true).then((taskSnippet: string) => {
        showCopyDialog(YamlHelperConstants.TaskCIKey, taskSnippet);
    });
}

export class YamlJob {
    private _demands: any[];
    private _tasks: YamlTask[];
    private _poolName: string;
    private _poolIsHosted: boolean;
    private _refName: string;
    private _name: string;
    private _timeoutInMinutes: number = 0;
    private _variables: IDictionaryStringTo<string> = {};
    private _variablesComments: string = "";
    private _jobType: DeployPhaseTypes;
    private _dependencies: string[] = [];
    private _condition: string = empty;
    private _executionPlanType: ParallelExecutionTypes;

    constructor(refName: string, name: string, jobType: DeployPhaseTypes, tasks: YamlTask[], demands: any[], poolName: string, poolIsHosted: boolean,
                timeoutInMinutes: number, dependencies: string[], condition: string, executionPlanType: ParallelExecutionTypes) {
        this._refName = refName.replace(new RegExp(" "), "_"); // refName should not have spaces, but just in case, make sure we remove them.
        this._name = name;
        this._jobType = jobType;
        this._tasks = tasks || [];
        this._demands = demands;
        this._poolName = poolName;
        this._poolIsHosted = poolIsHosted;
        this._timeoutInMinutes = timeoutInMinutes;
        this._dependencies = dependencies;
        this._condition = condition;
        this._executionPlanType = executionPlanType;
    }

    public getValue(multiplePhases: boolean): IPromise<string> {
        let value: string = this._getExecutionPlanTypeComment();
        if (multiplePhases) {
            value += YamlHelperConstants.jobProperty + this._refName + newLine;

            // add displayName property
            value += indent(YamlHelperConstants.displayNameProperty) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + this._name + newLine;
        }

        const dependencyValue: string = this._getDependencies(multiplePhases);
        value += dependencyValue;

        if (this._condition && this._condition !== empty && multiplePhases) {
            value += addTailingSpaces("");
            value += YamlHelperConstants.conditionProperty + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + this._condition + newLine;
        }

        if (multiplePhases) {
            value += addTailingSpaces("");
        }
        if (this._jobType === DeployPhaseTypes.RunOnServer) {
            // agentless job.  "pool: server" syntax replaced deprecated "server: true"
            value += YamlHelperConstants.poolProperty + YamlHelperConstants.singleSpace + YamlHelperConstants.serverValue + newLine;
        }
        else {
            value += YamlHelperConstants.poolProperty + newLine;
            if (multiplePhases) {
                value += addTailingSpaces("");
            }
            value += indent(YamlHelperConstants.nameProperty) + this._poolName + newLine;
        }

        const demandsValue: string = this._getDemands(multiplePhases);
        value += demandsValue;
        if (demandsValue && demandsValue !== empty) {
            value += newLine;
        }

        if (this._timeoutInMinutes > 0) {
            if (multiplePhases) {
                value += addTailingSpaces("");
            }
            value += indent(YamlHelperConstants.timeoutInMinutesProperty) + YamlHelperConstants.colon
                + YamlHelperConstants.singleSpace + this._timeoutInMinutes + newLine + newLine;
        }

        return this._getTasks().then((result: string) => {
            return value + this._getVariablesText(multiplePhases) + result;
        });
    }

    private _getDemands(multiplePhases: boolean): string {
        let demands: string = "";
        if (this._demands && this._demands.length > 0) {
            const multiDemands = this._demands.length > 1;
            if (multiplePhases) {
                demands = addTailingSpaces(demands);
            }
            demands += indent(YamlHelperConstants.demandsProperty + YamlHelperConstants.colon);
            if (multiDemands) {
                demands += newLine;
            }

            this._demands.forEach((demand: string) => {
                if (demand !== empty && demand.length > 0) {
                    if (multiDemands) {
                        demands += indent("-", multiplePhases ? 4 : 2);
                    }
                    demands += YamlHelperConstants.singleSpace + demand + newLine;
                }
            });
        }
        return demands;
    }

    private _getDependencies(multiplePhases: boolean): string {
        let dependencies: string = "";
        if (this._dependencies && this._dependencies.length > 0) {
            const multiDependencies = this._dependencies.length > 1;
            if (multiplePhases) {
                dependencies = addTailingSpaces(dependencies);
            }

            dependencies += YamlHelperConstants.dependencyProperty + YamlHelperConstants.colon;

            if (multiDependencies) {
                dependencies += newLine;
            }

            this._dependencies.forEach((dependency: string) => {
                if (dependency !== empty && dependency.length > 0) {
                    if (multiDependencies) {
                        dependencies += indent("-", multiplePhases ? 2 : undefined);
                    }
                    dependencies += YamlHelperConstants.singleSpace + dependency + newLine;
                }
            });
        }
        return dependencies;
    }

    private _getTasks(): IPromise<string> {
        let tasksContent: string = "";
        if (this._tasks && this._tasks.length > 0) {
            const promises = [];
            let index: number = 0;
            this._tasks.forEach((task: YamlTask) => {
                promises.push(task.getValue());
                index++;
            });

            return Promise.all(promises).then((yamlTasks: string[]) => {
                yamlTasks.forEach((yamlTask: string) => {
                    tasksContent += yamlTask + newLine;
                });

                this._tasks.forEach((task: YamlTask) => {
                    this._variablesComments += task.getVariablesComments();
                    const taskVariables = task.getVariables();
                    const variablesKeys = Object.keys(this._variables);
                    for (const key in taskVariables) {
                        if (variablesKeys.length === 0 || !!this._variables[key]) {
                            this._variables[key] = taskVariables[key];
                        }
                    }
                });

                return tasksContent;
            });
        }
        else {
            return Promise.resolve(tasksContent);
        }
    }

    private _getVariablesText(multiplePhases: boolean): string {
        let variables = "";
        const variablesKeys = Object.keys(this._variables);
        if (variablesKeys.length > 0) {
            if (multiplePhases) {
                variables = addTailingSpaces(variables);
            }

            variables += YamlHelperConstants.VariablesProperty + YamlHelperConstants.colon + newLine;
            variablesKeys.forEach((key) => {
                if (multiplePhases) {
                    variables = addTailingSpaces(variables);
                }

                variables += indent(key) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + "\'" + this._variables[key].replace("'", "''") + "\'" + newLine;
            });
        }
        return (this._variablesComments || variables) ? this._variablesComments + variables + newLine : "";
    }

    private _getExecutionPlanTypeComment(): string {
        if (this._executionPlanType !== ParallelExecutionTypes.None) {
            return "#" + Resources.ParallelExecutionYamlComment + newLine + newLine;
        }

        return empty;
    }
}

export class YamlTask {
    private _name: string;
    private _displayName: string;
    private _majorVersion: string;
    private _inputs: IDictionaryStringTo<string>;
    private _taskDefinition: TaskDefinition;
    private _isTaskGroup: boolean = false;
    private _multiplePhases: boolean = false;
    private _enabled: boolean = true;
    private _continueOnError: boolean = false;
    private _condition: string;
    private _timeout: number = 0;
    private _retryCountOnTaskFailure: number = 0;
    private _taskIndex: number = 0;
    private _inputsStates: IDictionaryStringTo<boolean>;
    private _variables: IDictionaryStringTo<IDefinitionVariable>;
    private _processParameters: IDictionaryStringTo<string>;
    private _usedVariables: IDictionaryStringTo<string> = {};
    private _variablesComments: string = "";
    private _hasAlias: boolean = false;
    private _environmentVariables: IDictionaryStringTo<string>;
    private _skipSyncSources: boolean = false;

    constructor(inputs: IDictionaryStringTo<string>, taskDefinition: TaskDefinition, taskRefName: string, taskDisplayName: string, taskVersion: string,
                enabled: boolean, continueOnError: boolean, condition: string, timeout: number, inputsStates: IDictionaryStringTo<boolean>,
                variables: IDictionaryStringTo<IDefinitionVariable>, processParameters: IDictionaryStringTo<string>,
                environmentVariables: IDictionaryStringTo<string>, multiplePhases: boolean, taskIndex: number, skipSyncSources: boolean, retryCountOnTaskFailure: number) {
        this._majorVersion = this._getTaskVersion(taskVersion);
        this._inputs = inputs;
        this._taskDefinition = taskDefinition;
        this._enabled = enabled;
        this._continueOnError = continueOnError;
        this._condition = condition;
        this._timeout = timeout;
        this._multiplePhases = multiplePhases;
        this._taskIndex = taskIndex;
        this._inputsStates = inputsStates;
        this._variables = variables;
        this._processParameters = processParameters;
        this._displayName = taskDisplayName;
        this._environmentVariables = environmentVariables;
        this._skipSyncSources = skipSyncSources;
        this._retryCountOnTaskFailure = retryCountOnTaskFailure;

        // for contributed tasks
        if (this._taskDefinition.contributionIdentifier && this._taskDefinition.contributionIdentifier !== empty) {
            this._name = this._taskDefinition.contributionIdentifier + "." + this._taskDefinition.name;
        }
        else {
            this._name = this._taskDefinition ? this._taskDefinition.name : taskRefName;
        }

        if (this._taskDefinition && this._taskDefinition.definitionType === DefinitionType.metaTask) {
            this._isTaskGroup = true;
        }
    }

    public getValue(includeVariables?: boolean): IPromise<string> {
        let value: string = "";
        if (this._isTaskGroup) {
            value += "#" + Resources.TaskGroupViewYamlError + newLine;
            return Promise.resolve(value);
        }
        else {
            if (this._taskIndex === 0) {
                if (this._multiplePhases) {
                    value = addTailingSpaces(value);
                }
                value += YamlHelperConstants.stepsProperty + YamlHelperConstants.colon + newLine;

                if (this._skipSyncSources) {
                    value += YamlHelperConstants.skipSyncKey + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + YamlHelperConstants.skipSyncValue + newLine;
                }
            }

            if (this._multiplePhases) {
                value = addTailingSpaces(value);
            }

            const taskName = this._name + "@" + this._majorVersion;
            // Some tasks has an alias so it should be used instead of taskName@taskVersion
            const taskAlias = this._resolveTaskName(taskName);

            // if the task does not have an alias
            if (taskName === taskAlias) {
                value += YamlHelperConstants.taskProperty + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + taskName + newLine;
            }
            else {
                value += taskAlias + YamlHelperConstants.colon + YamlHelperConstants.singleSpace;
                this._hasAlias = true;
            }

            // add displayName
            let displayName = "";

            if (this._hasAlias) {
                if (this._multiplePhases) {
                    displayName += addTailingSpaces("");
                }

                displayName += indent(YamlHelperConstants.displayNameProperty) + YamlHelperConstants.colon
                    + YamlHelperConstants.singleSpace + escapeValue(this._displayName) + newLine;
            }
            else {
                if (this._multiplePhases) {
                    value = addTailingSpaces(value);
                }
                value += indent(YamlHelperConstants.displayNameProperty) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + escapeValue(this._displayName) + newLine;
            }

            const controlOptions = this._getControlOptions();

            const environmentVariables = this._getEnvironmentVariables();

            return this._getInputs().then((inputsValue: string) => {
                let variablesText = "";
                if (includeVariables) {
                    variablesText = this._getVariablesText();
                }

                // Bug #1687993 
                // if using one of the command line tasks and there are no inputs, we at least need a newline before printing displayName, etc
                if (this._hasAlias && inputsValue.indexOf(newLine) < 0) {
                    inputsValue = newLine;
                }

                return variablesText + value + inputsValue + displayName + controlOptions + environmentVariables;
            });
        }
    }

    public getVariables(): IDictionaryStringTo<string> {
        return this._usedVariables;
    }

    public getVariablesComments(): string {
        return this._variablesComments;
    }

    private _resolveTaskName(taskName: string): string {
        const taskAlias = YamlHelperConstants.TasksAliasLookup[taskName];

        // Bug 1379960
        // Powershell and Bash scripts using file path type allow passing in arguments. However the alias YAML syntax for these
        // tasks do not support passing arguments. We will not use the alias if the task is file path based.
        if ((taskName === "PowerShell@2" || taskName === "Bash@3") &&
            this._inputs[YamlHelperConstants.TargetTypeInputName] &&
            this._inputs[YamlHelperConstants.TargetTypeInputName] === YamlHelperConstants.TargetTypeInputFilePathValue) {
            return taskName;
        }

        // Feedback ticket 1841973: there is a special case for powershell task alias
        // If 'pwsh' input is false (default), it shouldn't be specified and the alias should be '- powershell: <script>'
        // If 'pwsh' input is true, it shouldn't be specified either because in this case we have to use a different alias: '- pwsh: <script>'
        if (
            (taskName === "PowerShell@2")
            && this._inputs[YamlHelperConstants.PwshInputName]
            && this._inputs[YamlHelperConstants.PwshInputName] === Boolean.trueString
        ) {
            return YamlHelperConstants.PwshAlias;
        }

        if (taskAlias) {
            return taskAlias;
        }
        else {
            return taskName;
        }
    }

    private _getControlOptions(): string {
        let enabledProperty = "";
        // add enabled property only if the task is disabled
        if (!this._enabled) {
            if (this._multiplePhases) {
                enabledProperty = addTailingSpaces(enabledProperty);
            }

            enabledProperty += indent(YamlHelperConstants.enabledProperty) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + this._enabled + newLine;
        }

        let continueOnErrorProperty = "";
        // add contineOnError property only if the task has continueOnError set to true
        if (this._continueOnError) {
            if (this._multiplePhases) {
                continueOnErrorProperty = addTailingSpaces(continueOnErrorProperty);
            }

            continueOnErrorProperty += indent(YamlHelperConstants.continueOnErrorProperty) + YamlHelperConstants.colon
                + YamlHelperConstants.singleSpace + this._continueOnError + newLine;
        }

        let conditionProperty = "";
        // add condition property only if the task condition has a non-default value (value other than succeeded)
        if (this._condition && this._condition !== Common.TaskConditions.Succeeded) {
            if (this._multiplePhases) {
                conditionProperty = addTailingSpaces(conditionProperty);
            }

            conditionProperty += indent(YamlHelperConstants.conditionProperty) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + this._condition + newLine;
        }

        let timeoutProperty = "";
        // add timeout property only if the task has timeout set to a positive value
        if (this._timeout > 0) {
            if (this._multiplePhases) {
                timeoutProperty = addTailingSpaces(timeoutProperty);
            }

            timeoutProperty += indent(YamlHelperConstants.timeoutInMinutesProperty) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + this._timeout + newLine;
        }

        let retryCountProperty = "";
        if (this._retryCountOnTaskFailure > 0) {
            if (this._multiplePhases) {
                retryCountProperty = addTailingSpaces(retryCountProperty);
            }

            retryCountProperty += indent(YamlHelperConstants.retryCountOnTaskFailure) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + this._retryCountOnTaskFailure + newLine;
        }

        return enabledProperty + continueOnErrorProperty + conditionProperty + timeoutProperty + retryCountProperty;
    }

    private _getVariablesText(): string {
        let variables = "";
        const variablesKeys = Object.keys(this._usedVariables);
        if (variablesKeys.length > 0) {
            variables += YamlHelperConstants.VariablesProperty + YamlHelperConstants.colon + newLine;
            variablesKeys.forEach((key) => {
                variables += indent(key) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + "\'" + this._usedVariables[key].replace("'", "''") + "\'" + newLine;
            });
        }

        return (this._variablesComments || variables) ? this._variablesComments + variables + newLine : "";
    }

    private _getEnvironmentVariables(): string {
        let environmentVariables: string = "";
        if (this._environmentVariables && Object.keys(this._environmentVariables).length > 0) {
            environmentVariables += indent(YamlHelperConstants.environmentVariablesProperty) + YamlHelperConstants.colon + newLine;
            Object.keys(this._environmentVariables).forEach((name: string) => {
                environmentVariables += indent(indent(name)) + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + this._environmentVariables[name] + newLine;
            });
        }
        return environmentVariables;
    }

    private _getInputs(): IPromise<string> {
        let inputs: string = "";
        if (this._inputs && Object.keys(this._inputs).length > 0) {
            const promises = [];

            Object.keys(this._inputs).forEach((key) => {
                if (key && key !== empty && this._inputs.hasOwnProperty(key) && this._inputs[key] && this._inputs[key] !== empty) {
                    if (!this._inputsStates[key]) {
                        const inputDefinition: TaskInputDefinition = this._getInputDefinition(key);
                        const isDefaultValue: boolean = inputDefinition && inputDefinition.defaultValue ? inputDefinition.defaultValue === this._inputs[key] : false;
                        // for bash commands if the target type is 'inline', it does not need to be added to the yaml script
                        const isTargetTypeInline: boolean = key === YamlHelperConstants.TargetTypeInputName && this._inputs[key] === YamlHelperConstants.TargetTypeInputInlineValue;

                        if (this._hasAlias && key === YamlHelperConstants.PwshInputName) {
                            // Feedback ticket 1841973
                            // We never have to specify 'pwsh' input explicitly if we have PowerShell task alias:
                            // If 'pwsh' === false we don't need it because false is the default value
                            // If 'pwsh' === true we use 'pwsh' alias instead of 'powershell' and don't need to specify 'pwsh' as an input
                            // Correct alias is determined in _resolveTaskName method
                            return;
                        }

                        if ((key === YamlHelperConstants.TargetTypeInputName && isDefaultValue) || (!isDefaultValue && !isTargetTypeInline)) {
                            promises.push(this._getInputValue(key, inputDefinition));
                        }
                    }
                }
            });

            return Promise.all(promises).then((inputValues: YamlInput[]) => {
                inputValues.forEach((input: YamlInput) => {

                    const inputNameValue = input.getName();
                    const isScriptInput = inputNameValue.toLowerCase() === YamlHelperConstants.ScriptInputName;
                    const isInputFileTargetInput = inputNameValue === YamlHelperConstants.TargetTypeInputName;
                    const isScriptFileInput = inputNameValue === YamlHelperConstants.TargetTypeInputFilePathValue;

                    if (this._hasAlias && (isScriptInput || isInputFileTargetInput || isScriptFileInput)) {
                        inputs += input.getValue() + newLine;
                    }
                    else {
                        let inputName = indent(inputNameValue);
                        if (!this._hasAlias) {
                            if (this._multiplePhases) {
                                inputName = indent(inputName, 4);
                            }
                            else {
                                inputName = indent(inputName);
                            }

                        }
                        else if (this._multiplePhases) {
                            inputName = indent(inputName);
                        }

                        inputs += inputName + YamlHelperConstants.colon + YamlHelperConstants.singleSpace + input.getValue() + newLine;
                    }

                    // remove the double quotes around the value
                    const inputValue = this._removeCharsFromBegining(input.getValue());
                    const value = this._removeFromEnd(inputValue, inputValue.length - 1);
                    if (value && value.indexOf(YamlHelperConstants.VariablePrefix) >= 0) {
                        const variableNames: string[] = this._getVariableNames(value);
                        for (const variableIndex in variableNames) {
                            let variableName = variableNames[variableIndex];
                            // add each needed variable once
                            const variablesKeys = Object.keys(this._usedVariables);
                            if (variablesKeys.length === 0 || !this._usedVariables[variableName]) {
                                if (this._variables && !!this._variables[variableName]) {
                                    if (!this._variables[variableName].isSecret && !this._variables[variableName].allowOverride) {
                                        this._usedVariables[variableName] = this._variables[variableName].value;
                                    }
                                    else {
                                        if (this._variables[variableName].isSecret) {
                                            this._variablesComments += "#" + format(Resources.SecretVariableYamlComment, variableName) + newLine;
                                        }
                                        else {
                                            this._variablesComments += "#" + format(Resources.OverrideVariableYamlComment, variableName) + newLine;
                                        }
                                    }
                                }
                                else {
                                    if (variableName.startsWith(YamlHelperConstants.ProcessParametersPrefix)) {
                                        variableName = variableName.substring(YamlHelperConstants.ProcessParametersPrefix.length);
                                    }
                                    if (this._processParameters && (!!this._processParameters[variableName] || !!this._processParameters[variableName.toLowerCase()])) {
                                        this._usedVariables[variableName] = this._processParameters[variableName] || this._processParameters[variableName.toLowerCase()];
                                    }
                                    else if (!this._isPredefinedVariable(variableName)) {
                                        this._variablesComments += "#" + format(Resources.UndefinedVariableYamlComment, variableName) + newLine;
                                    }
                                }
                            }
                        }
                    }
                });

                if (inputs !== empty) {
                    if (!this._hasAlias) {
                        inputs = indent(YamlHelperConstants.inputsProperty) + YamlHelperConstants.colon + newLine + inputs;
                    }
                    if (this._multiplePhases && !this._hasAlias) {
                        inputs = indent(inputs);
                    }
                }
                return inputs;
            });
        }
        else {
            return Promise.resolve(inputs);
        }
    }

    private _isPredefinedVariable(variableName: string): boolean {
        let predefined = false;
        YamlHelperConstants.PredefinedVariablesPrefixes.some((prefix: string) => {
            if (startsWith(variableName, prefix, ignoreCaseComparer)) {
                predefined = true;
                return true;
            }
        });

        return predefined;

    }

    // Removes count number of characters from the begining of the string
    private _removeCharsFromBegining(inputValue: string, count: number = 1): string {
        if (!inputValue) {
            return inputValue;
        }

        while (count > 0 && inputValue) {
            inputValue = inputValue.substr(1);
            count--;
        }

        return inputValue;
    }

    private _removeFromEnd(inputValue: string, index: number): string {
        if (!inputValue || inputValue.length <= index || index === -1) {
            return inputValue;
        }

        return inputValue.substr(0, index);
    }

    private _getVariableNames(inputValue: string): string[] {
        const variableNames = [];
        if (inputValue) {
            let currentIndex = inputValue.indexOf("$(");
            let guardIndex = 0;
            while (currentIndex !== -1 && guardIndex < inputValue.length) {
                guardIndex++;

                // remove everything before $(varName)
                let value = inputValue.substr(currentIndex);
                // remove $(
                value = this._removeCharsFromBegining(value, 2);
                // remove everything after the varName
                currentIndex = value.indexOf(")");
                variableNames.push(this._removeFromEnd(value, currentIndex));
                currentIndex = inputValue.indexOf(")");

                // make sure it is a valid var name
                if (currentIndex !== -1) {
                    inputValue = inputValue.substr(currentIndex + 1);
                    currentIndex = inputValue.indexOf("$(");
                }
            }
        }

        return variableNames;
    }

    private _getInputDefinition(inputId: string): TaskInputDefinition {
        let inputDefinition: TaskInputDefinition = null;

        if (this._taskDefinition && this._taskDefinition.inputs) {
            this._taskDefinition.inputs.forEach((input) => {
                if (input.name === inputId) {
                    inputDefinition = input;
                }
            });
        }

        return inputDefinition;
    }

    private _getInputType(inputDefinition: TaskInputDefinition): string {
        return inputDefinition ? DtcUtils.getTaskInputType(inputDefinition) : "";
    }

    private _getInputValue(inputId: string, inputDefinition: TaskInputDefinition): IPromise<YamlInput> {
        let value = this._inputs[inputId];
        const inputType: string = this._getInputType(inputDefinition);

        if (inputType === InputControlType.INPUT_TYPE_CONNECTED_SERVICE.toLowerCase() || inputType === InputControlType.INPUT_TYPE_AZURE_CONNECTION.toLowerCase()) {
            return this._getConnectedServiceInputValue(inputDefinition);
        }
        else if (inputType === InputControlType.INPUT_TYPE_SECURE_FILE.toLowerCase()) {
            const secureFileStore = StoreManager.GetStore<SecureFilesStore>(SecureFilesStore);
            const secureFiles = secureFileStore && secureFileStore.getSecureFiles();
            const matchingSecureFile = secureFiles && secureFiles.find(sf => sf.id === value);
            const valueToExport = (matchingSecureFile && matchingSecureFile.name) || value;
            return Promise.resolve(new YamlInput(inputDefinition, valueToExport, this._multiplePhases, this._hasAlias));
        }
        else {
            // for bash commmands if it uses filepath we need to add "./" before the file path
            if (inputId === YamlHelperConstants.TargetTypeInputFilePathValue
                && this._inputs[YamlHelperConstants.TargetTypeInputName] === YamlHelperConstants.TargetTypeInputFilePathValue) {
               // verify that there isn't a leading "./" on the associated path
               if (value && !value.trim().startsWith("./")) {
                   value = "./" + value;
               }
            }
            return Promise.resolve(new YamlInput(inputDefinition, value, this._multiplePhases, this._hasAlias));
        }
    }

    private _getConnectedServiceInputValue(inputDefinition: TaskInputDefinition): IPromise<YamlInput> {
        const inputId = inputDefinition.name;
        const inputValues = this._inputs[inputId].split(",");
        const connectionNamesPromises = [];
        // in case the value is comma separated list then get the connection name for each of the connection ids
        inputValues.forEach((inputValue: string) => {
            if (inputValue && inputValue.trim() !== empty) {
                connectionNamesPromises.push(this._getSingleConnectionServiceEndpointName(inputValue.trim()));
            }
        });

        return Promise.all(connectionNamesPromises).then((connectionNames: string[]) => {
            let connectionName = empty;
            connectionNames.forEach((value: string) => {
                if (connectionName !== empty) {
                    connectionName += ", ";
                }
                connectionName += value;
            });
            return new YamlInput(inputDefinition, connectionName, this._multiplePhases, this._hasAlias);
        });
    }

    private _getSingleConnectionServiceEndpointName(connectionId: string): IPromise<string> {
        return ConnectedServiceComponentUtility.getConnectedServiceOptions(connectionId, true, "Generic", "", {})
            .then((connectedServiceInputBaseState: IConnectedServiceInputStateBase) => {
                return ConnectedServiceComponentUtility.getValueFromKey(connectedServiceInputBaseState.optionsMap, connectionId);
            }, (error) => {
                return empty;
            });
    }

    private _getTaskVersion(taskVersion: string): string {
        let version: string = "";
        if (taskVersion) {
            const dotIndex = taskVersion.indexOf(".");
            if (dotIndex > -1) {
                version = taskVersion.substr(0, dotIndex);
            }
            else {
                version = taskVersion;
            }
        }
        return version;
    }

}

class YamlInput {
    private _name: string;
    private _value: string;

    constructor(inputDefinition: TaskInputDefinition, value: string, multiplePhases: boolean, doesTaskHasAlias: boolean) {
        this._name = (inputDefinition.aliases && inputDefinition.aliases.length > 0) ? inputDefinition.aliases[0] : inputDefinition.name;
        this._value = this._formatInputValue(value, multiplePhases, doesTaskHasAlias);
    }

    public getName(): string {
        return this._name;
    }

    public getValue(): string {
        return this._value;
    }

    private _formatInputValue(input: string, multiplePhases: boolean, doesTaskHasAlias: boolean): string {
        if (input.indexOf(lineFeed) > -1) {
            let newValue = "|";
            if (multiplePhases) {
                newValue = addTailingSpaces(newValue);
            }
            const lines = input.split(lineFeed);
            lines.forEach((line: string) => {
                if (doesTaskHasAlias && !multiplePhases) {
                    newValue += lineFeed + indent(line, 3);
                }
                else if (multiplePhases) {
                    newValue += lineFeed + indent(line, 7);
                }
                else {
                    newValue += lineFeed + indent(line, 5);
                }
            });

            return newValue;
        }
        else {
           return escapeValue(input);
        }
    }
}

function indent(input: string, count?: number): string {
    if (count) {
        while (count > 0) {
            input = YamlHelperConstants.singleSpace + input;
            count--;
        }

        return input;
    }
    else {
        return YamlHelperConstants.spaces + input;
    }
}

function addTailingSpaces(input: string): string {
    return input + YamlHelperConstants.spaces;
}

namespace YamlHelperConstants {
    export const spaces: string = "  ";
    export const singleSpace: string = " ";
    export const jobProperty: string = "- job: ";
    export const poolProperty: string = "pool:";
    export const serverValue: string = "server";
    export const nameProperty: string = "name: ";
    export const vmImageProperty: string = "vmImage: ";
    export const jobsProperty: string = "jobs:";
    export const demandsProperty: string = "demands";
    export const dependencyProperty: string = "dependsOn";
    export const stepsProperty: string = "steps";
    export const inputsProperty: string = "inputs";
    export const environmentVariablesProperty: string = "env";
    export const enabledProperty: string = "enabled";
    export const continueOnErrorProperty: string = "continueOnError";
    export const conditionProperty: string = "condition";
    export const timeoutInMinutesProperty: string = "timeoutInMinutes";
    export const displayNameProperty: string = "displayName";
    export const taskProperty: string = "- task";
    export const colon: string = ":";
    export const resourcesKey: string = "resources";
    export const ProcessCIKey: string = "process";
    export const repoKey: string = "- repo";
    export const cleanOption: string = "clean";
    export const lfsOption: string = "lfs";
    export const fetchDepthOption: string = "fetchDepth";
    export const jobCIKey: string = "job";
    export const skipSyncKey: string = "- checkout";
    export const skipSyncValue: string = "none";
    export const syncSourcesValue: string = "self";
    export const TaskCIKey: string = "task";
    export const VariablesProperty: string = "variables";
    export const VariablePrefix: string = "$";
    export const ProcessParametersPrefix: string = "Parameters.";
    export const TasksAliasLookup: IDictionaryStringTo<string> = {"CmdLine@2": "- script", "PowerShell@2": "- powershell", "Bash@3": "- bash"};
    export const TargetTypeInputName: string = "targetType";
    export const TargetTypeInputInlineValue: string = "inline";
    export const TargetTypeInputFilePathValue: string = "filePath";
    export const PredefinedVariablesPrefixes: string[] = ["agent.", "build.", "common.", "release.", "system."];
    export const ScriptInputName: string = "script";
    export const PwshInputName: string = "pwsh";
    export const PwshAlias: string = "- pwsh";
    export const retryCountOnTaskFailure: string = "retryCountOnTaskFailure";
}

export function escapeValue(value: string): string {
    if (/[^A-Za-z0-9\./.]/.test(value)) {
        return "'" + value.replace(/'/g, "''") + "'";
    }

    return value;
}
